// src/components/admin/Category/CategoryTabs.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Assuming your shadcn/ui components are located here as per your file structure
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'; //

import InfoTab from './InfoTab';
import ParentageTab from './ParentageTab';
import AliasesTab from './AliasesTab';
import EditQnA from './EditQna';
import PricingTab from './PricingTab';
import LogsTab from './LogsTab';
import SearchTermsTab from './SearchTermsTab'; // <-- 1. Import your new component

// Define the available tabs
const TABS_CONFIG = [
  { key: 'info', label: 'Info', component: InfoTab },
  { key: 'parentage', label: 'Parentage', component: ParentageTab },
  { key: 'alias', label: 'Aliases', component: AliasesTab },
  { key: 'edit-qna', label: 'Edit Q&A', component: EditQnA },
  { key: 'pricing', label: 'Pricing', component: PricingTab },
  { key: 'search-terms', label: 'Search Terms', component: SearchTermsTab },
  { key: 'logs', label: 'Logs', component: LogsTab },
];

const isValidTabKey = (key) => TABS_CONFIG.some(tab => tab.key === key);

export function CategoryTabs({ category, categoryList, questionList, locale }) {
  const navigate = useNavigate();
  
  const { locale: paramsLocale, id: paramsCategoryIdFromUrl, tab: paramsTabKeyFromUrl } = useParams();
  console.log("In Category Tabs")
  const currentLocale = locale || paramsLocale;
  const categoryId = category ? category.id : null;

  const determineActiveTab = useCallback(() => {
    // This component instance is for a specific category, or for creating a new one (via ParentageTab standalone)
    // If category prop is present and has an id, it means we are in the locale of viewing/editing that category.
    if (category && category.id) {
      if (paramsTabKeyFromUrl && isValidTabKey(paramsTabKeyFromUrl)) {
        return paramsTabKeyFromUrl;
      }
      // If category.id from prop matches URL id, and tab is invalid/missing, default.
      if (paramsCategoryIdFromUrl === category.id && TABS_CONFIG[0]?.key) {
        return TABS_CONFIG[0].key;
      }
    }
    // Fallback or initial default if no specific locale implies a tab from URL
    return TABS_CONFIG[0]?.key;
  }, [category, paramsCategoryIdFromUrl, paramsTabKeyFromUrl]);

  const [activeTab, setActiveTab] = useState(determineActiveTab());

  // Effect to synchronize activeTab with URL changes or category locale changes.
  useEffect(() => {
    const newActiveTabDetermination = determineActiveTab();
    if (newActiveTabDetermination !== activeTab) {
      setActiveTab(newActiveTabDetermination);
    }

    // Ensure URL consistency if category is present
    if (category && category.id && paramsCategoryIdFromUrl === category.id) {
      const currentUrlTabIsValid = isValidTabKey(paramsTabKeyFromUrl);
      const targetTab = currentUrlTabIsValid ? paramsTabKeyFromUrl : TABS_CONFIG[0]?.key;

      if (targetTab && paramsTabKeyFromUrl !== targetTab) {
        // If URL tab is invalid or missing, and we have a valid category, correct the URL.
        navigate(`/admin/categories/manage/${currentLocale}/view/${category.id}/${targetTab}`, { replace: true }); // Use currentLocale
      }
    }
  }, [paramsTabKeyFromUrl, paramsCategoryIdFromUrl, category, activeTab, navigate, determineActiveTab,currentLocale]);


  const handleTabChange = (newTabKey) => {
    setActiveTab(newTabKey);
    if (category && category.id && currentLocale) { // Added currentLocale check
      navigate(`/admin/categories/manage/${currentLocale}/view/${category.id}/${newTabKey}`); // Use currentLocale
    }
  };

  // If no category is effectively selected (passed as prop),
  // render only the ParentageTab for creation flows.
  if (!category || !category.id) {
    return <ParentageTab category={null} locale={currentLocale}/>; // categoryList and questionList are not used by ParentageTab directly
  }

  // If a category is selected, render the Tabs interface
  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full mt-4">
      {/* Default shadcn/ui TabsList styling is responsive. Customize if needed. */}
      <TabsList>
        {TABS_CONFIG.map(tabInfo => (
          <TabsTrigger key={tabInfo.key} value={tabInfo.key}>
            {tabInfo.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {TABS_CONFIG.map(tabInfo => {
        const TabComponent = tabInfo.component;
        // Prepare props for the TabComponent
        let componentProps = { category, locale: currentLocale }; // Common prop

        if(tabInfo.key === 'info')
        {
          componentProps.categoryList = categoryList;
        }
        // Add specific props required by certain tabs
        if (tabInfo.key === 'pricing') {
          componentProps.questionList = questionList;
        }
        // LogsTab might not need any props, or specific ones
        if (tabInfo.key === 'logs') {
          // LogsTab was designed to take no props in your previous version
          componentProps = {};
        }
        // ParentageTab uses a hook for categoryList, no need to pass here unless its design changes.        
        // AliasesTab, EditQnA, InfoTab primarily need the 'category' prop.
        console.log("CategoryTabs Rendering State:", {
            activeTab: activeTab,
            currentLocale: currentLocale,
            categoryId: categoryId,
            paramsCategoryIdFromUrl: paramsCategoryIdFromUrl,
            paramsTabKeyFromUrl: paramsTabKeyFromUrl
          });

        return (
          <TabsContent key={tabInfo.key} value={tabInfo.key} className="mt-4">
            {TabComponent ? (
              <TabComponent {...componentProps} />
            ) : (
              <p className="text-destructive p-4">
                Error: Tab component for '{tabInfo.label}' is not configured correctly.
              </p>
            )}
          </TabsContent>
        );
      })}
    </Tabs>
  );
}